import React, { useEffect, useMemo, useState } from "react";
import OTPInput from "react-otp-input";
import { useLocation, useNavigate } from "react-router-dom";

import logo from "../../../assets/image/logo.png";
import otpBgTopRight from "../../../assets/image/otp-bg-top-right.png";
import otpBgBottomLeft from "../../../assets/image/otp-bg-bottom-left.png";
import apiCall from "../../../utils/axiosCall";
import { endPoint } from "../../../utils/endPoint";
import {
  saveFlowStage,
  saveBootstrapState,
} from "./flowStorage";
import "./OTPVerificationStyle.css";

function resolveRoleFromRoomInfo(roomInfo, userId) {
  if (!roomInfo || !userId) {
    return { role: "", rawRole: "" };
  }

  const staffId = roomInfo?.staff?.uuid;
  const candidateId = roomInfo?.user?.uuid;

  if (userId === staffId) {
    return { role: "interviewer", rawRole: "staff" };
  }
  if (userId === candidateId) {
    return { role: "candidate", rawRole: "candidate" };
  }
  return { role: "", rawRole: "" };
}

function formatScheduleText(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function canJoinRoomNow(roomInfo) {
  if (roomInfo?.end_datetime) {
    const endDateTime = new Date(roomInfo.end_datetime);
    if (!Number.isNaN(endDateTime.getTime()) && Date.now() > endDateTime.getTime()) {
      return false;
    }
  }

  if (!roomInfo?.join_allowed_from) {
    return roomInfo?.can_join_now !== false;
  }

  const joinAllowedAt = new Date(roomInfo.join_allowed_from);
  if (Number.isNaN(joinAllowedAt.getTime())) {
    return roomInfo?.can_join_now !== false;
  }

  return Date.now() >= joinAllowedAt.getTime();
}

export default function OTPVerification() {
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const roomId = searchParams.get("room") || "";
  const userId = searchParams.get("id") || "";
  const isJoinAllowedNow = canJoinRoomNow(roomInfo);
  const isInterviewExpired = useMemo(() => {
    if (!roomInfo?.end_datetime) {
      return false;
    }

    const endDateTime = new Date(roomInfo.end_datetime);
    if (Number.isNaN(endDateTime.getTime())) {
      return false;
    }

    return Date.now() > endDateTime.getTime();
  }, [roomInfo]);

  useEffect(() => {
    if (!roomId || !userId) {
      return undefined;
    }

    window.history.pushState(null, "", window.location.href);
    const otpRoute = `/external-interview-otp-screen?id=${userId}&room=${roomId}`;
    const handlePopState = () => {
      navigate(otpRoute, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, roomId, userId]);

  useEffect(() => {
    async function bootstrap() {
      setError("");
      if (!roomId || !userId) {
        setError("Invalid interview link: room and user id are required.");
        return;
      }

      setIsLoading(true);
      try {
        const basePayload = { room_id: roomId, user_id: userId };

        const verifyLinkResponse = await apiCall({
          url: endPoint.EXTERNAL_INTERVIEW_VERIFY_LINK,
          method: "post",
          data: JSON.stringify(basePayload),
        });

        if (verifyLinkResponse?.return_code !== "fetched_data_successfully") {
          setError("Link verification failed or link has expired.");
          return;
        }

        const roomInfoResponse = await apiCall({
          url: endPoint.EXTERNAL_INTERVIEW_ROOM_INFO,
          method: "post",
          data: JSON.stringify(basePayload),
        });

        if (roomInfoResponse?.return_code !== "fetched_data_successfully") {
          setError("Unable to fetch interview room details.");
          return;
        }

        const result = roomInfoResponse?.result || {};
        setRoomInfo(result);

        const resolvedRole = resolveRoleFromRoomInfo(result, userId);
        if (!resolvedRole.role) {
          setError("User does not belong to this interview room.");
          return;
        }

        localStorage.setItem("resourceful_video_call_id", userId);
        localStorage.setItem("resourceful_video_call_room", roomId);
        localStorage.setItem("interview_id", result?.interview_id || "");
        localStorage.setItem("session_id", result?.session_id || "");
        localStorage.setItem("is_interview_completed", result?.is_interview_completed || "");
        localStorage.setItem("interview_status", result?.interview_status || "");
        localStorage.setItem("staff_name", result?.staff?.name || "");
        localStorage.setItem("user_name", result?.user?.name || "");
        localStorage.setItem("staff_uuid", result?.staff?.uuid || "");
        localStorage.setItem("user_uuid", result?.user?.uuid || "");
        localStorage.setItem("userType", resolvedRole.rawRole);
        localStorage.setItem("externalInterviewRole", resolvedRole.role);
        localStorage.setItem("externalInterviewEmail", userId);
        localStorage.setItem("externalInterviewStartDateTime", result?.start_datetime || "");
        localStorage.setItem("externalInterviewEndDateTime", result?.end_datetime || "");
        localStorage.setItem("externalInterviewJoinAllowedFrom", result?.join_allowed_from || "");
        localStorage.setItem("externalInterviewDuration", String(result?.duration || ""));

        saveBootstrapState({
          roomId,
          userId,
          userType: resolvedRole.role,
          rawUserType: resolvedRole.rawRole,
          sessionId: result?.session_id || "",
        });

        if (result?.end_datetime) {
          const endDateTime = new Date(result.end_datetime);
          if (!Number.isNaN(endDateTime.getTime()) && Date.now() > endDateTime.getTime()) {
            setError("This interview time has already ended. You can no longer join.");
            return;
          }
        }

        if (!canJoinRoomNow(result)) {
          const allowedAt = formatScheduleText(result?.join_allowed_from);
          setError(
            allowedAt
              ? `You can join only 5 minutes before the interview start time. Please come back at ${allowedAt}.`
              : "You can join only 5 minutes before the interview start time."
          );
        }
      } catch (requestError) {
        console.log("[EXTERNAL_INTERVIEW_OTP_BOOTSTRAP_ERROR]", requestError);
        setError("Failed to initialize interview room.");
      } finally {
        setIsLoading(false);
      }
    }

    bootstrap();
  }, [navigate, roomId, userId]);

  const handleSubmit = async () => {
    setError("");
    if (!otp || otp.length < 6) {
      setError("Please enter the 6-digit password.");
      return;
    }
    if (!roomInfo) {
      setError("Room details are not loaded yet.");
      return;
    }
    if (isInterviewExpired) {
      setError("This interview time has already ended. You can no longer join.");
      return;
    }

    if (!isJoinAllowedNow) {
      const allowedAt = formatScheduleText(roomInfo?.join_allowed_from);
      setError(
        allowedAt
          ? `You can join only 5 minutes before the interview start time. Please come back at ${allowedAt}.`
          : "You can join only 5 minutes before the interview start time."
      );
      return;
    }

    const resolvedRole = resolveRoleFromRoomInfo(roomInfo, userId);
    if (!resolvedRole.role) {
      setError("Unable to resolve user role for this room.");
      return;
    }

    setIsLoading(true);
    try {
      const verifyOtpResponse = await apiCall({
        url: endPoint.EXTERNAL_INTERVIEW_VERIFY_PASSWORD,
        method: "post",
        data: JSON.stringify({
          room_id: roomId,
          user_id: userId,
          otp,
        }),
      });

      if (
        verifyOtpResponse?.return_code !== "fetched_data_successfully" ||
        verifyOtpResponse?.result?.detail !== "The link is valid"
      ) {
        setError("Invalid OTP.");
        return;
      }

      const roomSessionId = roomInfo?.session_id || "";
      saveBootstrapState({
        roomId,
        userId,
        userType: resolvedRole.role,
        rawUserType: resolvedRole.rawRole,
        sessionId: roomSessionId,
      });

      const roleLabel = resolvedRole.role.toUpperCase();
      console.log(`[${roleLabel}][OTP_VERIFIED]`, { roomId, userId });
      const query = new URLSearchParams({
        userId,
        userType: resolvedRole.role,
        rawUserType: resolvedRole.rawRole,
        sessionId: roomSessionId,
      });
      saveBootstrapState({
        roomId,
        userId,
        userType: resolvedRole.role,
        rawUserType: resolvedRole.rawRole,
        sessionId: roomSessionId,
      });
      saveFlowStage(roomId, userId, "guidelines");
      navigate(`/external-interview-v2-guidelines/${roomId}?${query.toString()}`, {
        replace: true,
      });
    } catch (verifyError) {
      console.log("[EXTERNAL_INTERVIEW_OTP_VERIFY_ERROR]", verifyError);
      setError("OTP verification failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className='otp-verification-page'>
        <div className='otp-bg-layer' aria-hidden='true'>
          <img src={otpBgTopRight} alt='' className='otp-bg-pattern otp-bg-pattern--top-right' />
          <img src={otpBgBottomLeft} alt='' className='otp-bg-pattern otp-bg-pattern--bottom-left' />
        </div>

        <div className='otp-login-box'>
          <img src={logo} alt='Resourceful' className='otp-logo' />

          <div className='otp-content'>
            <div className='otp-welcome-text'>
              <h1>Ready to Join?</h1>
              <p>Enter OTP to Join Interview</p>
            </div>

            <div className='otp-fields'>
              <OTPInput
                value={otp}
                onChange={(value) => setOtp(value)}
                numInputs={6}
                skipDefaultStyles
                containerStyle='otp-input-row'
                renderSeparator={null}
                renderInput={(inputProps, index) => (
                  <input
                    {...inputProps}
                    id={index === 0 ? "external-interview-otp" : undefined}
                    className={`otp-input-box ${
                      otp[index] ? "otp-input-box--filled" : "otp-input-box--empty"
                    }`}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSubmit();
                      }
                    }}
                  />
                )}
              />

              {error ? <p className='otp-error'>{error}</p> : null}

              <button
                type='button'
                className='otp-verify-button'
                onClick={handleSubmit}
                disabled={isLoading || !isJoinAllowedNow || isInterviewExpired}
              >
                {isLoading ? "Please wait..." : "Verify OTP"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className='otp-footer'>
        <p>
          Note: If you find difficulties joining this video call, we suggest you update your
          browser to the latest version. Get in touch with us if you still find difficulties
          joining the call.
        </p>
      </footer>
    </>
  );
}
